"use client";

import {
  Category2,
  DocumentText1,
  EmptyWallet,
  Heart,
  MessageQuestion,
  Messages2,
  Note,
} from "iconsax-reactjs";
import { useLocale, useTranslations } from "next-intl";
import ArandaoLogo from "@/assets/logo.svg";
import { ConnectButton, LocaleSwitcher } from "@/components/core";
import { DirectionalChevron } from "@/components/core/DirectionalChevron";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useSidebar } from "@/context/sidebar";
import { useWallet } from "@/context/wallet";
import { useRouter } from "@/i18n/navigation";
import { getDirection, type Locale } from "@/lib/i18n";
import { useLazyAppKit } from "@/lib/lazy-appkit";

export const Sidebar = () => {
  const t = useTranslations("nav");
  const { isOpen, setIsOpen } = useSidebar();
  const { isConnected } = useWallet();
  const { open } = useLazyAppKit();
  const router = useRouter();
  // Drawer slides in from the reading-start edge: left in LTR, right in RTL.
  const drawerSide =
    getDirection(useLocale() as Locale) === "rtl" ? "right" : "left";

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent
        title={t("menu")}
        className="pt-16! bg-bg-primary! px-0!"
        side={drawerSide}
      >
        <div className="flex items-center space-x-4 px-6  pb-6">
          <div className="flex items-center justify-center">
            <ArandaoLogo
              className="size-[28px] text-foreground"
              aria-label="logo"
            />
          </div>
          <ConnectButton>{t("connectWallet")}</ConnectButton>
          {isConnected && (
            <Button
              className="ms-auto"
              variant={"light"}
              size={"icon"}
              onClick={() => open()}
            >
              <EmptyWallet size={20} variant="Linear" />
            </Button>
          )}
        </div>
        <div className="flex flex-col">
          <div className="p-6">
            <LocaleSwitcher
              className="w-full"
              onSelect={() => setIsOpen(false)}
            />
          </div>
          <Separator />
          <div
            className="transition-all duration-300 flex items-center justify-between p-6 cursor-pointer hover:bg-bg-secondary/20"
            onClick={() => {
              setIsOpen(false);
              router.push("/profile/orders");
            }}
          >
            <div className="flex items-center gap-x-2">
              <DocumentText1 size={16} variant="Linear" />
              <p className="text-sm">{t("purchaseHistory")}</p>
            </div>
          </div>
          <div className="transition-all duration-300 flex items-center justify-between p-6 hover:bg-bg-secondary/20">
            <div className="flex items-center gap-x-2">
              <Heart size={16} variant="Linear" />
              <p className="text-sm">{t("favorites")}</p>
            </div>
          </div>
          <Separator />
          <div
            className="transition-all duration-300 flex items-center justify-between p-6 cursor-pointer hover:bg-bg-secondary/20"
            onClick={() => {
              setIsOpen(false);
              router.push("/search?is_product=true");
            }}
          >
            <div className="flex items-center gap-x-2">
              <Category2 size={16} variant="Linear" />
              <p className="text-sm">{t("products")}</p>
            </div>
            <DirectionalChevron size={16} />
          </div>
          <div
            className="transition-all duration-300 flex items-center justify-between p-6 cursor-pointer hover:bg-bg-secondary/20"
            onClick={() => {
              setIsOpen(false);
              router.push("/search?is_product=false");
            }}
          >
            <div className="flex items-center gap-x-2">
              <Category2 size={16} variant="Linear" />
              <p className="text-sm">{t("services")}</p>
            </div>
            <DirectionalChevron size={16} />
          </div>
          <Separator />
          <div
            className="transition-all duration-300 flex items-center justify-between p-6 cursor-pointer hover:bg-bg-secondary/20"
            onClick={() => {
              setIsOpen(false);
              router.push("/info/guide");
            }}
          >
            <div className="flex items-center gap-x-2">
              <MessageQuestion size={16} variant="Linear" />
              <p className="text-sm">{t("guide")}</p>
            </div>
          </div>
          <div
            className="transition-all duration-300 flex items-center justify-between p-6 cursor-pointer hover:bg-bg-secondary/20"
            onClick={() => {
              setIsOpen(false);
              router.push("/blog");
            }}
          >
            <div className="flex items-center gap-x-2">
              <Note size={16} variant="Linear" />
              <p className="text-sm">{t("blog")}</p>
            </div>
            <DirectionalChevron size={16} />
          </div>
          <Separator />
          <div
            className="transition-all duration-300 flex items-center justify-between p-6 cursor-pointer hover:bg-bg-secondary/20"
            onClick={() => {
              setIsOpen(false);
              router.push("/feedback");
            }}
          >
            <div className="flex items-center gap-x-2">
              <Messages2 size={16} variant="Linear" />
              <p className="text-sm">{t("submitFeedback")}</p>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
